/**
 * ProctoringService — Browser-based Safe Assessment Browser (SAB) equivalent.
 *
 * Enforces the following controls WITHOUT requiring any app installation:
 *  1. Force fullscreen mode + detect exit
 *  2. Detect multiple screens
 *  3. Restrict screen sharing (intercepts getDisplayMedia)
 *  4. Default screen protection (CSS user-select)
 *  5. Detect Developer Tools open
 *  6. Detect screenshot key (Print Screen)
 *  7. Prevent Copy / Cut / Paste
 *  8. Block context menu
 *  9. Tab-switch & window-blur detection
 * 10. AI cheating tool detection (rapid focus-steal pattern used by Cluely, Interview Coder etc.)
 *
 * Designed for candidate participants only (participantType === 'c').
 * All detected violations are surfaced via the onViolation callback.
 */

export interface ProctoringViolation {
  type: string;
  count: number;
  details: string;
}

// Exhaustive list of known violation types – used for whitelist validation on the receiver side.
export const PROCTOR_VIOLATION_TYPES = [
  'FULLSCREEN_EXIT',
  'TAB_SWITCH',
  'WINDOW_BLUR',
  'COPY_ATTEMPT',
  'CUT_ATTEMPT',
  'PASTE_ATTEMPT',
  'CONTEXT_MENU',
  'DEVTOOLS_KEY',
  'SOURCE_VIEW',
  'SCREENSHOT_KEY',
  'META_KEY',
  'ALT_TAB',
  'MULTI_SCREEN',
  'AI_TOOL_SUSPECTED',
  'DEVTOOLS_OPEN',
  'SCREEN_SHARE_ATTEMPT',
] as const;

export type ProctoringViolationType = typeof PROCTOR_VIOLATION_TYPES[number];

export class ProctoringService {
  violations: Array<{ type: string; timestamp: Date; details: string }> = [];

  private isActive: boolean = false;
  private tabSwitchCount: number = 0;

  // Listener references kept for proper cleanup
  private _onVisibilityChange: () => void;
  private _onWindowBlur: () => void;
  private _onFullscreenChange: () => void;
  private _onContextMenu: (e: Event) => void;
  private _onCopy: (e: Event) => void;
  private _onCut: (e: Event) => void;
  private _onPaste: (e: Event) => void;
  private _onKeyDown: (e: KeyboardEvent) => void;
  private _onFocusLoss: () => void;
  private _onFocusGain: () => void;

  private _devToolsTimer: any;
  private _fullscreenCheckTimer: any;
  private _screenCheckTimer: any;

  private _originalGetDisplayMedia: ((...args: any[]) => Promise<MediaStream>) | null = null;
  private _chimeSharePermitted: boolean = false;

  constructor(private onViolation: (v: ProctoringViolation) => void) {}

  start(): void {
    if (this.isActive) return;
    this.isActive = true;
    this._applyScreenProtection();
    this._requestFullscreen();
    // this._listenFullscreenChange();
    // this._startFullscreenEnforcement();
    this._listenVisibilityChange();
    this._listenWindowBlur();
    this._blockCopyPasteContextMenu();
    this._blockRestrictedKeyboardShortcuts();
    this._detectMultipleScreens();
    this._detectAICheatingTools();
    this._restrictScreenShare();
  }

  stop(): void {
    if (!this.isActive) return;
    this.isActive = false;

    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    window.removeEventListener('blur', this._onWindowBlur);

    ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'].forEach(evt => {
      document.removeEventListener(evt, this._onFullscreenChange);
    });

    document.removeEventListener('contextmenu', this._onContextMenu);
    document.removeEventListener('copy', this._onCopy);
    document.removeEventListener('cut', this._onCut);
    document.removeEventListener('paste', this._onPaste as EventListener);
    document.removeEventListener('keydown', this._onKeyDown);

    if (this._onFocusLoss) window.removeEventListener('blur', this._onFocusLoss);
    if (this._onFocusGain) window.removeEventListener('focus', this._onFocusGain);

    if (this._devToolsTimer) clearInterval(this._devToolsTimer);
    if (this._fullscreenCheckTimer) clearInterval(this._fullscreenCheckTimer);
    if (this._screenCheckTimer) clearInterval(this._screenCheckTimer);

    this._restoreScreenShare();
    this._removeScreenProtection();
    this._exitFullscreen();
    this._hideProctoringOverlay();
  }

  /**
   * Permit exactly one getDisplayMedia call — used by the Chime content-share
   * flow so candidates can share their screen within the meeting. Any call that
   * arrives without this permit is still blocked and logged as a violation.
   */
  permitNextChimeShare(): void {
    this._chimeSharePermitted = true;
  }

  reEnterFullscreen(): void {
    this._hideProctoringOverlay();
    this._requestFullscreen();
  }

  // Private – Violation Logging 
  private _logViolation(type: ProctoringViolationType, details: string): void {
    const violation = { type, timestamp: new Date(), details };
    this.violations.push(violation);
    const count = this.violations.filter(v => v.type === type).length;
    if (this.onViolation) {
      this.onViolation({ type, count, details });
    }
  }

  // Fullscreen

  private _requestFullscreen(): void {
    const el = document.documentElement as any;
    try {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
      else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
      else if (el.msRequestFullscreen) el.msRequestFullscreen();
    } catch (e) { /* Browser may reject if not triggered by user gesture */ }
  }

  private _exitFullscreen(): void {
    const doc = document as any;
    try {
      if (doc.exitFullscreen) doc.exitFullscreen();
      else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
      else if (doc.mozCancelFullScreen) doc.mozCancelFullScreen();
      else if (doc.msExitFullscreen) doc.msExitFullscreen();
    } catch (e) {
      console.warn('Failed to exit fullscreen', e);
    }
  }

  private _isFullscreen(): boolean {
    const doc = document as any;
    return !!(doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement || doc.msFullscreenElement);
  }

  private _listenFullscreenChange(): void {
    this._onFullscreenChange = () => {
      if (!this._isFullscreen() && this.isActive) {
        this._logViolation('FULLSCREEN_EXIT', 'Candidate exited fullscreen mode');
        this._showFullscreenWarning();
      }
    };
    ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'].forEach(evt => {
      document.addEventListener(evt, this._onFullscreenChange);
    });
  }

  private _startFullscreenEnforcement(): void {
    this._fullscreenCheckTimer = setInterval(() => {
      if (this.isActive && !this._isFullscreen()) {
        this._showFullscreenWarning();
      }
    }, 3000);
  }

  private _showFullscreenWarning(): void {
    const overlay = document.getElementById('proctoring-fs-overlay');
    if (overlay) overlay.style.display = 'flex';
  }

  private _hideProctoringOverlay(): void {
    const overlay = document.getElementById('proctoring-fs-overlay');
    if (overlay) overlay.style.display = 'none';
  }

  // Tab Switch / Window Blur

  private _listenVisibilityChange(): void {
    this._onVisibilityChange = () => {
      if (document.hidden && this.isActive) {
        this.tabSwitchCount++;
        this._logViolation('TAB_SWITCH', `Tab hidden or window minimized (total: ${this.tabSwitchCount})`);
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
  }

  private _listenWindowBlur(): void {
    this._onWindowBlur = () => {
      if (this.isActive && !document.hidden) {
        this._logViolation('WINDOW_BLUR', 'Window lost focus — possible switch to another application');
      }
    };
    window.addEventListener('blur', this._onWindowBlur);
  }

  // Copy / Paste / Context Menu

  private _blockCopyPasteContextMenu(): void {
    this._onCopy = (e: Event) => {
      e.preventDefault();
      this._logViolation('COPY_ATTEMPT', 'Copy attempt blocked');
    };
    this._onCut = (e: Event) => {
      e.preventDefault();
      this._logViolation('CUT_ATTEMPT', 'Cut attempt blocked');
    };
    this._onPaste = (e: Event) => {
      e.preventDefault();
      this._logViolation('PASTE_ATTEMPT', 'Paste attempt blocked');
    };
    this._onContextMenu = (e: Event) => {
      e.preventDefault();
      this._logViolation('CONTEXT_MENU', 'Right-click context menu blocked');
    };
    document.addEventListener('copy', this._onCopy);
    document.addEventListener('cut', this._onCut);
    document.addEventListener('paste', this._onPaste as EventListener);
    document.addEventListener('contextmenu', this._onContextMenu);
  }

  // Keyboard Restrictions

  private _blockRestrictedKeyboardShortcuts(): void {
    this._onKeyDown = (e: KeyboardEvent) => {
      // DevTools: F12
      if (e.key === 'F12') {
        e.preventDefault();
        this._logViolation('DEVTOOLS_KEY', 'F12 (DevTools) key pressed');
        return;
      }
      // DevTools: Ctrl+Shift+I / Ctrl+Shift+J / Ctrl+Shift+C
      if (e.ctrlKey && e.shiftKey && ['i', 'I', 'j', 'J', 'c', 'C'].includes(e.key)) {
        e.preventDefault();
        this._logViolation('DEVTOOLS_KEY', `DevTools shortcut blocked: Ctrl+Shift+${e.key}`);
        return;
      }
      // View Source: Ctrl+U
      if (e.ctrlKey && (e.key === 'u' || e.key === 'U')) {
        e.preventDefault();
        this._logViolation('SOURCE_VIEW', 'View source (Ctrl+U) blocked');
        return;
      }
      // Screenshot: Print Screen
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        this._logViolation('SCREENSHOT_KEY', 'Print Screen key pressed');
        return;
      }
      // Windows / Command key
      if (e.key === 'Meta' || e.key === 'OS') {
        e.preventDefault();
        this._logViolation('META_KEY', 'Windows/Meta key pressed');
        return;
      }
      // Alt+Tab — cannot prevent but log it
      if (e.altKey && e.key === 'Tab') {
        this._logViolation('ALT_TAB', 'Alt+Tab key combination detected');
      }
    };
    document.addEventListener('keydown', this._onKeyDown);
  }

  // Multiple Screens

  private _detectMultipleScreens(): void {
    const checkScreens = () => {
      const s = window.screen as any;
      // Modern Screen API (Chrome 111+)
      if (typeof s.isExtended !== 'undefined' && s.isExtended) {
        this._logViolation('MULTI_SCREEN', 'Multiple displays detected via Screen API');
        return;
      }
      // Heuristic fallback: available width significantly exceeds reported display width
      if (window.screen.availWidth > window.screen.width * 1.5) {
        this._logViolation('MULTI_SCREEN', 'Multiple displays detected (heuristic)');
      }
    };

    checkScreens();
    this._screenCheckTimer = setInterval(checkScreens, 15000);

    // Listen for screen topology changes (modern browsers)
    const s = window.screen as any;
    if (s && typeof s.addEventListener === 'function') {
      try {
        s.addEventListener('change', checkScreens);
      } catch (e) {
        console.warn('Screen change listener is not supported', e);
      }
    }
  }

  // CSS Screen Protection

  private _applyScreenProtection(): void {
    if (document.getElementById('proctoring-styles')) return;
    const style = document.createElement('style');
    style.id = 'proctoring-styles';
    style.textContent = `
      body.proctoring-active {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
    `;
    document.head.appendChild(style);
    document.body.classList.add('proctoring-active');
  }

  private _removeScreenProtection(): void {
    const style = document.getElementById('proctoring-styles');
    if (style) style.remove();
    document.body.classList.remove('proctoring-active');
  }

  // AI Cheating Tool Detection
  // Cluely and Interview Coder work by briefly stealing window focus (<500 ms).
  // We detect this pattern by timing the blur→focus cycle.

  private _detectAICheatingTools(): void {
    let focusLossTime = 0;

    this._onFocusLoss = () => { focusLossTime = Date.now(); };
    this._onFocusGain = () => {
      const elapsed = Date.now() - focusLossTime;
      // Rapid focus change under 500 ms is a strong signal of an overlay tool
      if (focusLossTime > 0 && elapsed > 0 && elapsed < 500) {
        this._logViolation('AI_TOOL_SUSPECTED', `Rapid focus change (${elapsed}ms) — possible AI overlay tool`);
      }
      focusLossTime = 0;
    };

    window.addEventListener('blur', this._onFocusLoss);
    window.addEventListener('focus', this._onFocusGain);

    // DevTools open detection via window outer/inner dimension gap
    this._devToolsTimer = setInterval(() => {
      if (!this.isActive) return;
      const widthDiff = window.outerWidth - window.innerWidth;
      const heightDiff = window.outerHeight - window.innerHeight;
      if (widthDiff > 160 || heightDiff > 160) {
        this._logViolation('DEVTOOLS_OPEN', 'Developer tools likely open (window size anomaly)');
      }
    }, 5000);
  }

  // Screen Share Restriction
  // Overrides navigator.mediaDevices.getDisplayMedia to block screen capture.

  private _restrictScreenShare(): void {
    const mediaDevices = navigator.mediaDevices as any;
    if (!mediaDevices || !mediaDevices.getDisplayMedia) return;
    this._originalGetDisplayMedia = mediaDevices.getDisplayMedia.bind(mediaDevices);
    mediaDevices.getDisplayMedia = (...args: any[]): Promise<MediaStream> => {
      if (this._chimeSharePermitted) {
        // One-shot permit: allow Chime's own content-share call and immediately revoke
        this._chimeSharePermitted = false;
        return this._originalGetDisplayMedia!(...args);
      }
      this._logViolation('SCREEN_SHARE_ATTEMPT', 'Screen capture attempt blocked by proctoring');
      return Promise.reject(new DOMException('Screen sharing is not allowed during this interview', 'NotAllowedError'));
    };
  }

  private _restoreScreenShare(): void {
    const mediaDevices = navigator.mediaDevices as any;
    if (this._originalGetDisplayMedia && mediaDevices) {
      mediaDevices.getDisplayMedia = this._originalGetDisplayMedia;
      this._originalGetDisplayMedia = null;
    }
  }
}
